import type { SectionProps } from "../types";

const topTechnologies = [
  { icon: "ri-reactjs-line", name: "React" },
  { icon: "ri-code-s-slash-line", name: "JavaScript" },
  { icon: "ri-brain-line", name: "Machine Learning" },
  { icon: "ri-terminal-box-line", name: "Python" },
  { icon: "ri-database-2-line", name: "Database" },
  { icon: "ri-cloud-line", name: "Cloud Computing" },
  { icon: "ri-bar-chart-box-line", name: "Data Science" },
  { icon: "ri-node-tree", name: "Node.js" },
  { icon: "ri-smartphone-line", name: "Mobile Dev" },
  { icon: "ri-robot-line", name: "AI & Automation" },
];

const bottomTechnologies = [
  { icon: "ri-git-branch-line", name: "Git & GitHub" },
  { icon: "ri-window-line", name: "Full Stack" },
  { icon: "ri-lock-2-line", name: "Cybersecurity" },
  { icon: "ri-pie-chart-line", name: "Analytics" },
  { icon: "ri-rocket-line", name: "DevOps" },
  { icon: "ri-bubble-chart-line", name: "Blockchain" },
  { icon: "ri-lightbulb-flash-line", name: "Innovation" },
  { icon: "ri-team-line", name: "Agile & Scrum" },
  { icon: "ri-global-line", name: "Web Technologies" },
  { icon: "ri-palette-line", name: "UI/UX Design" },
];

const features = [
  { icon: "ri-team-line", text: "Học từ chuyên gia" },
  { icon: "ri-flask-line", text: "80% thực hành" },
  { icon: "ri-community-line", text: "Cộng đồng 500+" },
  { icon: "ri-award-line", text: "Chứng chỉ uy tín" },
  { icon: "ri-briefcase-line", text: "Hỗ trợ việc làm" },
  { icon: "ri-time-line", text: "Học linh hoạt" },
];

export default function VisionSection({ isVisible, sectionRef }: SectionProps) {
  return (
    <section
      ref={sectionRef}
      data-section="vision"
      className="py-20 sm:py-24 md:py-32 lg:py-40 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/4 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-cyan-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-400/10 rounded-full blur-3xl"></div>
      </div>

      {/* Scrolling Text Top */}
      <div className="absolute top-0 left-0 w-full py-2 sm:py-3 md:py-4 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 overflow-hidden border-y border-cyan-500/20">
        <div className="flex whitespace-nowrap animate-marquee">
          <div className="flex items-center gap-4 sm:gap-6 md:gap-8 text-cyan-600 font-bold text-xs sm:text-sm md:text-base lg:text-lg">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 sm:gap-6 md:gap-8">
                {topTechnologies.map((tech, idx) => (
                  <span key={idx} className="flex items-center gap-1.5 sm:gap-2">
                    <i className={`${tech.icon} text-base sm:text-lg md:text-xl lg:text-2xl`}></i> {tech.name}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrolling Text Bottom */}
      <div className="absolute bottom-0 left-0 w-full py-2 sm:py-3 md:py-4 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 overflow-hidden border-y border-blue-500/20">
        <div className="flex whitespace-nowrap animate-marquee-reverse">
          <div className="flex items-center gap-4 sm:gap-6 md:gap-8 text-blue-600 font-bold text-xs sm:text-sm md:text-base lg:text-lg">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 sm:gap-6 md:gap-8">
                {bottomTechnologies.map((tech, idx) => (
                  <span key={idx} className="flex items-center gap-1.5 sm:gap-2">
                    <i className={`${tech.icon} text-base sm:text-lg md:text-xl lg:text-2xl`}></i> {tech.name}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
          {/* Left: Decorative Globe */}
          <div
            className={`relative transition-all duration-1000 mb-8 lg:mb-0 ${
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            <div className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto aspect-square">
              {/* Outer orbit rings with breathe animation */}
              <div
                className="absolute inset-0 border border-cyan-400/20 sm:border-2 rounded-full animate-breathe"
                style={{ animationDuration: "4s" }}
              ></div>
              <div
                className="absolute inset-4 sm:inset-6 border border-blue-400/20 sm:border-2 rounded-full animate-breathe-reverse"
                style={{ animationDuration: "4s", animationDelay: "0.5s" }}
              ></div>

              {/* Center circle - Logo Only */}
              <div className="absolute inset-8 sm:inset-10 md:inset-12 bg-white rounded-full shadow-2xl flex items-center justify-center overflow-hidden p-4 sm:p-6 md:p-8 border-2 sm:border-4 border-cyan-400/20">
                {/* StackWay Logo */}
                <div className="relative z-10 w-full h-full flex items-center justify-center p-2 sm:p-3 md:p-4">
                  <img
                    src={`${
                      typeof window !== "undefined"
                        ? window.location.origin
                        : ""
                    }/stackway/logo/logo-academy.png`}
                    alt="StackWay Academy"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Floating particles */}
              <div className="absolute top-1/4 right-0 w-2 h-2 sm:w-4 sm:h-4 bg-cyan-400 rounded-full animate-ping"></div>
              <div
                className="absolute bottom-1/3 left-0 w-2 h-2 sm:w-3 sm:h-3 bg-blue-400 rounded-full animate-ping"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute top-1/2 right-1/4 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-300 rounded-full animate-ping"
                style={{ animationDelay: "0.5s" }}
              ></div>

              {/* Orbiting icons */}
              <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 animate-float-slow">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center shadow-lg">
                  <i className="ri-code-s-slash-line text-white text-sm sm:text-base md:text-xl"></i>
                </div>
              </div>
              <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 animate-float-medium">
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                  <i className="ri-brain-line text-white text-sm sm:text-base md:text-lg"></i>
                </div>
              </div>
              <div className="absolute top-1/3 right-2 sm:right-4 animate-float-fast">
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg">
                  <i className="ri-database-2-line text-white text-sm sm:text-base md:text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Quote Text */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
          >
            {/* Quote icon */}
            <div className="text-cyan-400/20 mb-4 sm:mb-6 animate-pulse">
              <i className="ri-double-quotes-l text-4xl sm:text-5xl md:text-6xl lg:text-7xl"></i>
            </div>

            {/* Main quote */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight mb-6 sm:mb-8 italic">
              Không ngừng{" "}
              <span className="relative inline-block group pr-2">
                <span className="bg-gradient-to-r from-cyan-500 via-cyan-400 to-blue-600 bg-clip-text text-transparent font-black bg-[length:200%_auto] animate-text-shimmer pr-1">
                  sáng tạo
                </span>
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 group-hover:h-1 transition-all duration-300"></span>
              </span>
              , nghiên cứu và làm chủ các{" "}
              <span className="relative inline-block group pr-2">
                <span className="bg-gradient-to-r from-blue-600 via-blue-400 to-cyan-500 bg-clip-text text-transparent font-black bg-[length:200%_auto] animate-text-shimmer pr-2">
                  công nghệ mới
                </span>
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 group-hover:h-1 transition-all duration-300"></span>
              </span>
            </h2>

            {/* Sub text */}
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
              Công nghệ đang thay đổi thế giới và{" "}
              <strong className="text-[#2a4b7c]">Stackway Academy </strong>
              giúp bạn sẵn sàng cho sự thay đổi đó. Chúng tôi tin rằng mỗi người
              đều có tiềm năng trở thành kỹ sư công nghệ xuất sắc.
            </p>

            {/* Feature highlights */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 sm:gap-3 bg-white/50 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 hover:scale-105"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-white text-sm sm:text-base md:text-lg`}></i>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-700">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

